if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        const linesUpdate = this.startCartLinesUpdate(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              throw Object.assign(new Error(parsedState.errors), { code: 'INVALID' });
            }

            linesUpdate?.resolve(parsedState);
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((e) => {
            if (e.code !== 'INVALID') console.error(e);
            this.dispatchCartErrorEvent(
              e.code === 'INVALID' ? e.message : window.cartStrings.error,
              e.code || 'SERVICE_UNAVAILABLE'
            );
            linesUpdate?.reject(e);
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      /*
       * 2026-08-13 - CART COUNT DID NOT UPDATE AFTER A QUICK ADD. Bug fix.
       *
       * Reported from a phone: adding 100 units of Convivium Feliccia from the
       * collection grid left the cart icon showing nothing at all. Opening the
       * cart showed the line was there. So the add worked and only the header
       * failed to redraw - which is worse than it sounds, because a buyer who
       * cannot see a count has no way to tell whether his tap registered, and
       * will either tap again or open the cart every single time.
       *
       * CAUSE, found by reading rather than guessing. This theme uses the
       * cart NOTIFICATION, not the drawer: sections/cart-drawer.liquid is a
       * 29-byte stub and no element with id "CartDrawer" is ever rendered.
       * getSectionsToRender nevertheless still lists CartDrawer, inherited
       * from stock Dawn, and renderSections then ran
       *
       *     sectionElement.closest("cart-drawer")?.classList.toggle(...)
       *
       * on it. The optional chaining guards the RESULT of closest(), not the
       * receiver - sectionElement itself was null, so calling .closest on it
       * threw a TypeError. The throw happened inside the promise chain, so it
       * was swallowed by the .catch() below and reported as a generic cart
       * error rather than surfacing the real fault.
       *
       * FIX, two parts:
       *
       * 1. Guard on sectionElement before touching it. A section that is not
       *    on the page is not an error - it is a theme that does not use that
       *    section - so it is skipped rather than thrown on.
       *
       * 2. Redraw the bubble FIRST, before anything that can fail. The cart
       *    count is the one piece of feedback the buyer actually watches, so
       *    it must not depend on every other section rendering cleanly. This
       *    also covers the early return above it: if the queue is still
       *    draining, ids overlap and renderSections bails out before drawing
       *    anything, which is a second path to the same silent failure.
       *
       * Both are defensive rather than clever. The stock code assumed a DOM
       * this theme does not have, and assumed every section it asks for will
       * come back renderable.
       */
      updateCartIconBubble(parsedState) {
        const bubble = document.getElementById('cart-icon-bubble');
        const markup = parsedState.sections && parsedState.sections['cart-icon-bubble'];
        if (!bubble || !markup) return;

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const source = parsed.querySelector('.shopify-section') || parsed.body;
        if (source) bubble.innerHTML = source.innerHTML;
      }

      renderSections(parsedState, ids) {
        this.updateCartIconBubble(parsedState);

        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          if (section.section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
          }

          const nested = sectionElement.querySelector(section.selector);
          const elementToReplace = nested ? nested : sectionElement;
          const markup = parsedState.sections && parsedState.sections[section.section];

          if (elementToReplace && markup) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(markup, section.selector);
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`)?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
